import styled, { css } from 'styled-components';

import { ANIM, COLORS, SPACE } from '../../theme/tokens';
import { Text } from '../Text/Text';

export const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
`;

export const LabelRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${SPACE[12]};
`;

/** Groups an optional leading icon with the label so they stay together at the row's start. The muted
 *  colour tints the leading icon to match the field icons elsewhere; the Label sets its own text colour. */
export const LabelGroup = styled.div`
  display: flex;
  align-items: center;
  /* Off the scale on purpose: 6px keeps the icon tight against the label it marks, as Field's affixes. */
  gap: 6px;
  color: ${COLORS.textMuted};
`;

export const Label = styled(Text)`
  font-size: 14px;
  color: ${COLORS.text};
`;

/** Trailing hint beside the label (e.g. a live character count). */
export const Hint = styled.span`
  font-size: 12px;
  color: ${COLORS.textFaint};
  white-space: nowrap;
`;

/** Mirrors Field's InputRow surface, grown to a multiline control. `$autoGrow` makes it size to its
 *  content (native `field-sizing`, Chrome 123+) up to a cap, then scroll — so a long prompt is fully
 *  visible without a fixed scroll box. Browsers without `field-sizing` keep the resizable min-height. */
export const Input = styled.textarea<{ $autoGrow?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
  min-height: 96px;
  /* 14px: Field's block padding (off the scale on purpose), so the two read as one family. */
  padding: 14px ${SPACE[16]};
  border-radius: 12px;
  border: 1px solid ${COLORS.panelBorder};
  background: ${COLORS.surfaceInset};
  color: ${COLORS.text};
  font-size: 16px;
  font-family: inherit;
  line-height: 1.5;
  outline: none;
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  /* The browser's outline is dropped, so the border says where the typing goes. */
  &:focus {
    border-color: ${COLORS.accent};
  }

  ${(p) =>
    p.$autoGrow &&
    css`
      field-sizing: content;
      resize: none;
      max-height: 800px;
      overflow-y: auto;
    `}

  &::placeholder {
    color: ${COLORS.textFaint};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;
